import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import cacheManager from '../CacheManager';
import Select from 'react-select';
import { NumericFormat } from 'react-number-format';
import { formatDate } from '../js/dateFormatter';
import { showSuccess, showError } from '../utils/toast';

const PgFaturaEditar = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(true);

    const [clientesById, setClientesById] = useState({});
    const [itensById, setItensById] = useState({});
    const [faturas, setFaturas] = useState([]);

    const [fatura, setFatura] = useState({
        id_cliente: '',
        data_emissao: '',
        data_vencimento: '',
        valor_total: '',
        situacao: '',
        data_cancelamento: '',
        motivo_cancelamento: '',
        itens_json: '[]',

        fat_emissao: '',
        fat_nome: '',
        fat_cpf_cnpj: '',
        fat_endereco: '',
        fat_bairro: '',
        fat_cidade: '',
        fat_uf: '',
        fat_cep: '',
        fat_inscricao_estadual: '',
        fat_email: '',
        fat_telefone: '',
        fat_vencimento: '',
        fat_forma_pagamento: '',
        fat_finalidade: '',
        fat_valor: '',
        fat_numero_da_fatura: '',
    });
    const [pagamentos, setPagamentos] = useState([]);
    const [pagamentosFiltrados, setPagamentosFiltrados] = useState([]);
    const [agendamentos, setAgendamentos] = useState([]);
    const [agendamentosFiltrados, setAgendamentosFiltrados] = useState([]);

    const [itens, setItens] = useState([]);
    const [nomeItem, setNomeItem] = useState('');
    const [qntItem, setQntItem] = useState(1);
    const [valorItem, setValorItem] = useState(0);
    const [mostrarFaturaExterna, setMostrarFaturaExterna] = useState(false);

    const carregarCache = async () => {
        try {
            setIsLoading(true);
            const clientesById = await cacheManager.getClientesById();
            setClientesById(clientesById);
            const itensById = await cacheManager.getItensById();
            setItensById(itensById);
            const pagamentosCache = await cacheManager.getPagamentosCache();
            setPagamentos(pagamentosCache);
            const agendamentosCache = await cacheManager.getAgendamentosCache();
            setAgendamentos(agendamentosCache);
            const faturasCache = await cacheManager.getFaturasCache();
            setFaturas(faturasCache);
        } catch (error) {
            console.error('Erro ao buscar clientes:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const carregarDados = async () => {
            await carregarCache();

            if (id) {
                setIsLoading(true);
                fetch(`https://api.faeworks.com.br/fatura/read.php?id=${id}`)
                    .then(response => response.json())
                    .then(data => {
                        if (data && data.length > 0) {
                            const fatura = data[0];
                            setFatura({
                                id_cliente: fatura.id_cliente,
                                data_emissao: fatura.data_emissao,
                                data_vencimento: fatura.data_vencimento,
                                valor_total: fatura.valor_total,
                                situacao: fatura.situacao,
                                data_cancelamento: fatura.data_cancelamento ? fatura.data_cancelamento.split(' ')[0] : '',
                                motivo_cancelamento: fatura.motivo_cancelamento,
                                itens_json: fatura.itens_json || '[]',

                                fat_emissao: fatura.fat_emissao || '',
                                fat_nome: fatura.fat_nome || '',
                                fat_cpf_cnpj: fatura.fat_cpf_cnpj || '',
                                fat_endereco: fatura.fat_endereco || '',
                                fat_bairro: fatura.fat_bairro || '',
                                fat_cidade: fatura.fat_cidade || '',
                                fat_uf: fatura.fat_uf || '',
                                fat_cep: fatura.fat_cep || '',
                                fat_inscricao_estadual: fatura.fat_inscricao_estadual || '',
                                fat_email: fatura.fat_email || '',
                                fat_telefone: fatura.fat_telefone || '',
                                fat_vencimento: fatura.fat_vencimento || '',
                                fat_forma_pagamento: fatura.fat_forma_pagamento || '',
                                fat_finalidade: fatura.fat_finalidade || '',
                                fat_valor: fatura.fat_valor || '',
                                fat_numero_da_fatura: fatura.fat_numero_da_fatura || '',
                            });
                            const parsedItens = JSON.parse(fatura.itens_json || '[]');
                            setItens(Array.isArray(parsedItens) ? parsedItens : []);
                        } else {
                            showError('Erro ao buscar dados da fatura');
                        }
                    })
                    .catch(error => console.error('Erro ao buscar dados da fatura:', error))
                    .finally(() => setIsLoading(false));
            }

            try {
                const pagamentosFiltrados = pagamentos.filter(pagamento => pagamento.id_fatura === id);
                setPagamentosFiltrados(pagamentosFiltrados);
            } catch (error) {
                console.error('Erro ao buscar dados:', error);
            }

            try {
                const agendamentosFiltrados = agendamentos.filter(
                    agendamento => String(agendamento.id_fatura) === String(id)
                );
                setAgendamentosFiltrados(agendamentosFiltrados);
            } catch (error) {
                console.error('Erro ao buscar dados:', error);
            }
        };

        carregarDados();
    }, [id, pagamentos, agendamentos]);

    const atualizarFatura = async () => {
        try {
            setIsLoading(true);
            const url = id ? 'https://api.faeworks.com.br/fatura/update.php' : 'https://api.faeworks.com.br/fatura/create.php';
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id_fatura: id,
                    id_cliente: fatura.id_cliente,
                    data_emissao: fatura.data_emissao ? fatura.data_emissao : new Date().toISOString(),
                    data_vencimento: fatura.data_vencimento,
                    valor_total: fatura.valor_total,
                    situacao: fatura.situacao,
                    data_cancelamento: fatura.data_cancelamento,
                    motivo_cancelamento: fatura.motivo_cancelamento,
                    itens_json: JSON.stringify(itens),

                    fat_emissao: fatura.fat_emissao,
                    fat_nome: fatura.fat_nome,
                    fat_cpf_cnpj: fatura.fat_cpf_cnpj,
                    fat_endereco: fatura.fat_endereco,
                    fat_bairro: fatura.fat_bairro,
                    fat_cidade: fatura.fat_cidade,
                    fat_uf: fatura.fat_uf,
                    fat_cep: fatura.fat_cep,
                    fat_inscricao_estadual: fatura.fat_inscricao_estadual,
                    fat_email: fatura.fat_email,
                    fat_telefone: fatura.fat_telefone,
                    fat_vencimento: fatura.fat_vencimento,
                    fat_forma_pagamento: fatura.fat_forma_pagamento,
                    fat_finalidade: fatura.fat_finalidade,
                    fat_valor: fatura.fat_valor,
                    fat_numero_da_fatura: fatura.fat_numero_da_fatura,
                }),
            });
            const result = await response.json();
            if (result.status === 'success') {
                setIsLoading(false);
                showSuccess('Fatura atualizada com sucesso!');
                cacheManager.invalidateFaturasCache();
                handleVoltarClick();
            } else {
                setIsLoading(false);
                showError('Erro ao atualizar fatura: ' + result.message);
            }
        } catch (error) {
            console.error('Erro ao atualizar fatura:', error);
            setIsLoading(false);
            showError('Erro ao atualizar fatura.');
        }
    };

    // Estilos para esmaecer a página
    const overlayStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 2,
        display: isLoading ? 'block' : 'none',
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFatura({ ...fatura, [name]: value });
    };

    const handleValorChange = (values) => {
        const { floatValue } = values;
        setFatura({
            ...fatura,
            valor_total: floatValue
        });
    };

    const handlePreencherFaturaExternaClick = () => {
        setMostrarFaturaExterna(!mostrarFaturaExterna);
    };

    const obterProximoNumeroFaturaExterna = () => {
        const numerosFaturas = faturas
            .map(fatura => parseInt(fatura.fat_numero_da_fatura, 10))
            .filter(numero => !isNaN(numero));
    
        return 1 + (numerosFaturas.length > 0 ? Math.max(...numerosFaturas) : 0);
    };

    const handleVoltarClick = () => {
        navigate('/faturas');
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        await atualizarFatura();
    };

    const handleClienteChange = (selectedOption) => {
        setFatura({ ...fatura, id_cliente: selectedOption ? selectedOption.value : '' });
    };

    const clienteOptions = Object.keys(clientesById).map((key) => ({
        value: key,
        label: (clientesById[key].cpf ? clientesById[key].cpf + ' | ' : '') + clientesById[key].nome
    }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const somatorioValorAgendamento = agendamentosFiltrados.reduce((total, agendamento) => total + parseFloat(agendamento.valor), 0).toFixed(2);
    const somatorioValorPagamento = pagamentosFiltrados.reduce((total, pagamento) => total + parseFloat(pagamento.valor), 0).toFixed(2);
    const somatorioItens = (itens || []).reduce((total, item) => total + parseFloat(item.valor * item.quantidade), 0).toFixed(2);

    const handleAddItem = () => {
        if(nomeItem === '' || valorItem === ''){   
            return;
        }
        const newItem = {
          nome: nomeItem,
          quantidade: qntItem,
          valor: valorItem
        };
        setItens([...(itens || []), newItem]);
        setNomeItem('');
        setQntItem('1');
        setValorItem('0.00');
      };

      const handleRemoveItem = (index) => {
        const newItens = (itens || []).filter((_, i) => i !== index);
        setItens(newItens);
      };

    return (
        <div className="container mt-5">
            {isLoading && <div style={overlayStyle}></div>} {/* Overlay de carregamento */}
            <h1 className="h1">{id ? `Editar Fatura #${id}` : 'Nova Fatura'}</h1>
            <form onSubmit={handleSubmit} className="needs-validation" noValidate>
                <div className="row">
                    <div className="col-12 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Cliente:
                            <Select
                                className="form-select"
                                value={clienteOptions.find(option => option.value === String(fatura.id_cliente))}
                                onChange={handleClienteChange}
                                options={clienteOptions}
                                isClearable={false}
                            />
                        </label>
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Data de Emissão:
                            <div className="form-control" style={{ backgroundColor: '#e9ecef' }}>
                                {fatura.data_emissao ? new Date(fatura.data_emissao).toLocaleDateString('pt-BR') : new Date().toLocaleDateString('pt-BR')}
                            </div>
                        </label>
                    </div>
                    <div className="col-3 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Valor:
                            <NumericFormat
                                className="form-control"
                                name="valor_total"
                                value={fatura.valor_total}
                                onValueChange={handleValorChange}
                                thousandSeparator="."
                                decimalSeparator=","
                                decimalScale={2}
                                fixedDecimalScale={true}
                                allowNegative={false}
                                prefix={'R$ '}
                            />
                        </label>
                    </div>
                    <div className="col-3 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Valor calculado:
                            <NumericFormat
                                className="form-control"
                                name="valor_total"
                                value={Number(somatorioValorAgendamento) + Number(somatorioItens)}
                                thousandSeparator="."
                                decimalSeparator=","
                                decimalScale={2}
                                fixedDecimalScale={true}
                                allowNegative={false}
                                prefix={'R$ '}
                            />
                        </label>
                    </div>
                    
                    <div className="col-6 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Status:
                            <select
                                className="form-control"
                                name="situacao"
                                value={fatura.situacao}
                                onChange={handleInputChange}
                            >
                                <option value="Ativa">Ativa</option>
                                <option value="Cancelada">Cancelada</option>
                            </select>
                        </label>
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label" style={{ display: fatura.situacao === 'Cancelada' ? 'block' : 'none' }}>
                            Data do cancelamento:
                            <input
                                type="date"
                                className="form-control"
                                name="data_cancelamento"
                                value={fatura.data_cancelamento}
                                onChange={handleInputChange}
                            />
                        </label>
                    </div>
                    <div className="col-12 mb-3">
                    <label className="form-label" style={{ display: fatura.situacao === 'Cancelada' ? 'block' : 'none' }}>
                            Motivo do Cancelamento:
                            <textarea
                                className="form-control"
                                name="motivo_cancelamento"
                                value={fatura.motivo_cancelamento}
                                onChange={handleInputChange}
                            >
                            </textarea>
                        </label>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">
                        <hr />
                    </div>
                </div>
                <h3>Agendamentos</h3>
                <table className="table">
                    <thead className="table-light">
                        <tr>
                            <th>ID Agendamento</th>
                            <th>Agendamento</th>
                            <th>Data agendamento</th>
                            <th>Valor</th>
                            <th>Cliente</th>
                        </tr>
                    </thead>
                    <tbody>
                        {agendamentosFiltrados.map((agendamento, index) => (
                            <tr key={index}>
                            <td>
                                <Link to={`/agendamento/editar/${agendamento.id_agendamento}`} className="text-dark underline-on-hover">
                                {agendamento.id_agendamento}
                                </Link>
                            </td>
                            <td>{itensById[agendamento.id_item].titulo}</td>
                            <td>{formatDate(agendamento.data_inicio_agendamento)}</td>
                            <td>R$ {agendamento.valor}</td>
                            <td>{agendamento.id_cliente}</td>
                            </tr>
                        ))}
                        <tr>
                            <td colSpan="2"><strong>Total</strong></td>
                            <td colSpan="2"><strong>R$ {isNaN(somatorioValorAgendamento) ? 0 : somatorioValorAgendamento}</strong></td>
                        </tr>
                    </tbody>
                </table>
                <div className="row">
                    <div className="col-12">
                        <hr />
                    </div>
                </div>
                <h3>Itens</h3>
                <div className="row">
        <div className="col-3 mb-3">
          <label className="form-label">Nome do Item</label>
          <input
            type="text"
            className="form-control"
            value={nomeItem}
            onChange={(e) => setNomeItem(e.target.value)}
          />
        </div>
        <div className="col-3 mb-3">
          <label className="form-label">Quantidade</label>
          <input
            type="number"
            className="form-control"
            value={qntItem}
            onChange={(e) => setQntItem(e.target.value)}
            min="1"
          />
        </div>
        <div className="col-3 mb-3">
          <label className="form-label">Valor</label>
          <input
            type="number"
            className="form-control"
            value={valorItem}
            onChange={(e) => setValorItem(e.target.value)}
          />
        </div>
        <div className="col-3 mb-3">
          <label className="form-label">Total</label>
          <NumericFormat
            className="form-control"
            name="item_valor_total"
            value={valorItem*qntItem}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={2}
            fixedDecimalScale={true}
            allowNegative={false}
            prefix={'R$ '}
            disabled={true}
        />
        </div>
        <div className="col-12 mb-3">
          <button type="button" className="btn btn-primary float-end" onClick={handleAddItem}>Adicionar Item</button>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <ul className="list-group">
            {(itens || []).map((item, index) => (
                <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                <span>{item.nome} - Qnt: {item.quantidade} - Total: R$ {(item.valor * item.quantidade).toFixed(2)}</span>
                <button type="button" className="btn btn-danger btn-sm" onClick={() => handleRemoveItem(index)}>Remover</button>
                </li>
            ))}
          </ul>
          <strong>Total</strong> <strong>R$ {somatorioItens}</strong>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <hr />
        </div>
      </div>
                <h3>Pagamentos</h3>
                <table className="table">
                    <thead className="table-light">
                        <tr>
                            <th>ID Pagamento</th>
                            <th>Data Pagamento</th>
                            <th>Valor</th>
                            <th>Cliente</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pagamentosFiltrados.map((pagamento, index) => (
                            <tr key={index}>
                            <td>
                                <Link to={`/pagamento/editar/${pagamento.id_pagamento}`} className="text-dark underline-on-hover">
                                {pagamento.id_pagamento}
                                </Link>
                            </td>
                            <td>{formatDate(pagamento.data_pagamento)}</td>
                            <td>R$ {pagamento.valor}</td>
                            <td>{pagamento.id_cliente}</td>
                            </tr>
                        ))}
                        <tr>
                            <td colSpan="2"><strong>Total</strong></td>
                            <td colSpan="2"><strong>R$ {somatorioValorPagamento}</strong></td>
                        </tr>
                    </tbody>
                </table>
                <div className="row">
                    <div className="col-12">
                        <hr />
                    </div>
                </div>

                <div className="container mt-5">
            {/* Botão para exibir os campos da fatura externa */}
            <button
                type="button"
                className="btn btn-info mb-3"
                onClick={handlePreencherFaturaExternaClick}
            >
                {mostrarFaturaExterna
                ? 'Ocultar Fatura Externa'
                : fatura.fat_numero_da_fatura && (String(fatura.fat_numero_da_fatura).trim() !== '')
                ? 'Editar Fatura Externa'
                : 'Preencher Fatura Externa'}
            </button>

            {/* Campos da fatura externa */}
            {mostrarFaturaExterna && (
                <div className="row">
                    <div className="col-6 mb-3">
                        <label className="form-label">Data de Emissão</label>
                        <input
                            type="text"
                            className="form-control"
                            value={fatura.fat_emissao}
                            onChange={(e) => setFatura({ ...fatura, fat_emissao: e.target.value })}
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label">Nome</label>
                        <input
                            type="text"
                            className="form-control"
                            value={fatura.fat_nome}
                            onChange={(e) => setFatura({ ...fatura, fat_nome: e.target.value })}
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label">CPF/CNPJ</label>
                        <input
                            type="text"
                            className="form-control"
                            value={fatura.fat_cpf_cnpj}
                            onChange={(e) => setFatura({ ...fatura, fat_cpf_cnpj: e.target.value })}
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label">Endereço</label>
                        <input
                            type="text"
                            className="form-control"
                            value={fatura.fat_endereco}
                            onChange={(e) => setFatura({ ...fatura, fat_endereco: e.target.value })}
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label">Bairro</label>
                        <input
                            type="text"
                            className="form-control"
                            value={fatura.fat_bairro}
                            onChange={(e) => setFatura({ ...fatura, fat_bairro: e.target.value })}
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label">Cidade</label>
                        <input
                            type="text"
                            className="form-control"
                            value={fatura.fat_cidade}
                            onChange={(e) => setFatura({ ...fatura, fat_cidade: e.target.value })}
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label">UF</label>
                        <input
                            type="text"
                            className="form-control"
                            value={fatura.fat_uf}
                            onChange={(e) => setFatura({ ...fatura, fat_uf: e.target.value })}
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label">CEP</label>
                        <input
                            type="text"
                            className="form-control"
                            value={fatura.fat_cep}
                            onChange={(e) => setFatura({ ...fatura, fat_cep: e.target.value })}
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label">Inscrição Estadual</label>
                        <input
                            type="text"
                            className="form-control"
                            value={fatura.fat_inscricao_estadual}
                            onChange={(e) => setFatura({ ...fatura, fat_inscricao_estadual: e.target.value })}
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label">E-mail</label>
                        <input
                            type="email"
                            className="form-control"
                            value={fatura.fat_email}
                            onChange={(e) => setFatura({ ...fatura, fat_email: e.target.value })}
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label">Telefone</label>
                        <input
                            type="text"
                            className="form-control"
                            value={fatura.fat_telefone}
                            onChange={(e) => setFatura({ ...fatura, fat_telefone: e.target.value })}
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label">Data de Vencimento</label>
                        <input
                            type="text"
                            className="form-control"
                            value={fatura.fat_vencimento}
                            onChange={(e) => setFatura({ ...fatura, fat_vencimento: e.target.value })}
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label">Forma de Pagamento</label>
                        <input
                            type="text"
                            className="form-control"
                            value={fatura.fat_forma_pagamento}
                            onChange={(e) => setFatura({ ...fatura, fat_forma_pagamento: e.target.value })}
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label">Finalidade</label>
                        <input
                            type="text"
                            className="form-control"
                            value={fatura.fat_finalidade}
                            onChange={(e) => setFatura({ ...fatura, fat_finalidade: e.target.value })}
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label">Valor</label>
                        <input
                            type="text"
                            className="form-control"
                            value={fatura.fat_valor}
                            onChange={(e) => setFatura({ ...fatura, fat_valor: e.target.value })}
                        />
                    </div>
                    <div className="col-6 mb-3 d-flex align-items-end">
                        <div className="flex-grow-1">
                            <label className="form-label">Número da Fatura</label>
                            <input
                                type="text"
                                className="form-control"
                                value={fatura.fat_numero_da_fatura}
                                onChange={(e) => setFatura({ ...fatura, fat_numero_da_fatura: e.target.value })}
                            />
                        </div>
                        {(!fatura.fat_numero_da_fatura || (String(fatura.fat_numero_da_fatura).trim() === '')) && (
                            <button
                                type="button"
                                className="btn btn-primary ms-2"
                                onClick={() => {
                                    const novoNumeroFatura = `${obterProximoNumeroFaturaExterna()}`;
                                    setFatura({ ...fatura, fat_numero_da_fatura: novoNumeroFatura });
                                }}
                            >
                                Gerar Número
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>

                <div className="row">
                    <div className="col-12 d-flex justify-content-end">
                        <button
                            type="button"
                            className="btn btn-secondary me-2"
                            onClick={handleVoltarClick}
                        >
                            Voltar
                        </button>
                        <button type="submit" className="btn btn-primary">
                            Salvar
                        </button>
                    </div>
                </div>
            </form>
            
        </div>
    );
};

export default PgFaturaEditar;